import useSWR from 'swr';
import { useAuth } from '../contexts/AuthContext';
import { useApiClient } from '../utils/apiClient';

export interface Item {
  id: string;
  business_id: string;
  name: string;
  description?: string;
  rate: number; // base rate from items.rate
  is_taxable: boolean;
  category_id?: string;
  category_name?: string;
  category_color?: string;
  created_at: string;
  use_customer_rate?: boolean;
  // price-aware fields from backend when customer_id is provided
  standard_rate?: number;
  customer_rate?: number | null;
  effective_rate?: number;
}

interface CreateItemData {
  name: string;
  description?: string;
  rate: number;
  is_taxable?: boolean;
  category_id?: string;
}

// Accept update data without id; id will be passed separately to avoid accidental undefined URLs
type UpdateItemData = Partial<CreateItemData>;
interface BatchAssignCategoryResponse {
  updatedCount: number;
}

export function useItems(customerId?: string) {
  const { currentBusiness, API_BASE_URL } = useAuth();
  const { get, post, put, delete: deleteItem } = useApiClient();
  
  const fetcher = async ([url, businessId, custId]: [string, string, string | undefined]): Promise<Item[]> => {
    const params: any = {};
    if (custId) params.customer_id = custId;
    
    const response = await get<Item[]>(`${url}?${new URLSearchParams(params)}`);
    console.log('useItems: fetched items:', response);
    if (response.length > 0) {
      console.log('useItems: first item keys:', Object.keys(response[0]));
      console.log('useItems: first item id:', response[0].id);
    }
    return response;
  };

  const swrKey = currentBusiness ? ['/items', currentBusiness.id, customerId || undefined] : null;

  const { data: items = [], error, isLoading, mutate: mutateItems } = useSWR<Item[]>(
    swrKey,
    fetcher
  );

  const createItem = async (itemData: CreateItemData): Promise<Item> => {
    if (!currentBusiness) {
      throw new Error('No business context');
    }

    const response = await post<Item>('/items', itemData);

    // Optimistically update the cache
    await mutateItems([...items, response], false);
    
    return response;
  };

  const updateItem = async (id: string, itemData: UpdateItemData): Promise<Item> => {
    if (!currentBusiness) {
      throw new Error('No business context');
    }

    const response = await put<Item>(`/items/${id}`, itemData);

    // Optimistically update the cache
    const updatedItems = items.map((item: Item) => 
      item.id === id ? response : item
    );
    await mutateItems(updatedItems, false);
    
    return response;
  };

  const removeItem = async (itemId: string): Promise<void> => {
    if (!currentBusiness) {
      throw new Error('No business context');
    }

    await deleteItem(`/items/${itemId}`);

    // Optimistically update the cache
    const updatedItems = items.filter((item: Item) => item.id !== itemId);
    await mutateItems(updatedItems, false);
  };

  const batchAssignCategory = async (itemIds: string[], categoryId: string): Promise<number> => {
    if (!currentBusiness) {
      throw new Error('No business context');
    }
    if (!itemIds.length) {
      throw new Error('At least one item is required');
    }

    const response = await put<BatchAssignCategoryResponse>('/items/batch/category', {
      itemIds,
      categoryId,
    });

    // Revalidate to pick up category name/color joins from backend
    await mutateItems();
    return response.updatedCount || 0;
  };

  return {
    items,
    error,
    isLoading,
    createItem,
    updateItem,
    deleteItem: removeItem,
    batchAssignCategory,
    mutate: mutateItems,
  };
}; 